import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useMemo, useState } from 'react';
import useScrollToTopOnBookingChange from '../../../hooks/useScrollToTopOnBookingChange';
import Drawer from '@mui/material/Drawer';
import BookingField from './BookingDrawerField.react';
import CollapseText from '../../general/CollapseText.react';
import BookingDrawerReservationSummary from './BookingDrawerReservationSummary.react';
import BookingDrawerPaymentDetails from './BookingDrawerPaymentDetails.react';
import BookingDrawerTasId from './BookingDrawerTasId';
import FutureBenefits from './FutureBenefits';
import { Button, CircularProgress, IconButton, Snackbar, Tooltip } from '@mui/material';
import Analytics from 'arbitrip-common/client/analytics';
import ApiURLs from '../../../utils/ApiURLs';
import InvoiceIcon from './InvoiceIcon';
import VoucherIcon from './VoucherIcon';
import { ReactComponent as CopyIcon } from '../../../img/bookings/copy.svg';
import { ReactComponent as PointsIcon } from '../../../img/search_page/loyalty/present-plain.svg';
import { ReactComponent as BreakfastIcon } from '../../../img/amenities/breakfast@3x.svg';
import ReservationConstants from '../../../constants/ReservationConstants';
import CancelReservationDialog from './CancelReservationDialog.react';
import ReservationActions from '../../../actions/ReservationActions';
import TravelBoosterTitle from '../bookings/TravelBoosterTitle.react';
import TravelBooster from '../bookings/TravelBooster.react';
import RetroactivePointsUsageTitle from './RetroactivePointsUsageTitle.react';
import RetroactivePointsUsage from './RetroactivePointsUsage.react';
import MaterialDesignDialog from '../../general/MaterialDesignDialog.react';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Check from '@mui/icons-material/Check';
import { ReactComponent as FreeCancelationIcon } from '@/img/results_page/free_cancelation_icon.svg';
import { ReactComponent as BnplNoChargesIcon } from '@/img/icons/bnpl-no-charges.svg';
import BookingStatus from '../../../utils/BookingStatus';
import BookingsConstants from '../../../constants/BookingsConstants';
import { Catering } from 'arbitrip-common/client/utils';
import { getBreakfastIncluded } from '../requests/helpers';
import { PAYMENT_TYPE, PAYMENT_STATUS } from 'arbitrip-common/general/constants';
import Config from 'arbitrip-common/client/utils/Config';
import PriceBreakdown from 'arbitrip-common/client/utils/PriceBreakdown';
import { isRetroactivePointsUsageEnabled } from 'arbitrip-common/client/utils/RetroactivePoints';
import BookingDrawerPaymentBreakdownDetails from './BookingDrawerPaymentBreakdownDetails.react';
import {
	getIsUnpaid,
	getIsBnpl,
	isBankTransferLocked,
	isBankTransferPaid,
	getBankTransferLockTranslationKey,
	needsBankTransferCorrection,
	getBankTransferRejectionReason,
	getBankTransferRejectionComment,
	getBankTransferReferenceNumber,
} from '../../../utils/PayLater';
import SettlePaymentMain from './SettlePayment/SettlePaymentMain.react';
import { useTranslation } from 'react-i18next';
import { RTL } from 'arbitrip-common/translation/const';
import BookingDrawerGDSComponent from './BookingDrawerGDSComponent';
import { isElalCompany } from '@/utils/isCompany';
import TravelersField from './TravelersField';
import settlePaymentHelpers from './SettlePayment/helpers';

const date_format = 'D MMM YYYY';

const ESCAPE_KEY_CODE = 27;

const BNPL_CHARGE_REMINDER_DAYS = 6;

const BNPL_MAX_CHARGE_ATTEMPTS = 3;

function getVoucherURL(booking) {
	return ApiURLs.VOUCHER_PRINT.replace(':reservation_id', booking.id);
}

const BookingDrawer = ({
	open,
	onClose,
	booking,
	profile,
	sync_combtas_status,
	syncTravelBoosterStatus,
	confirmationStatus,
	points,
	updatePointsUsageStatus,
	settlePaymentStatus,
}) => {
	const { t, i18n } = useTranslation();

	const display_dev = Config.dev_mode || (Config.prod && profile.is_arbi_support);

	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showCancelSuccess, setShowCancelSuccess] = useState(false);
	const [showTravelBoosterModal, setShowTravelBoosterModal] = useState(false);
	const [showRetroactivePointsUsageModal, setShowRetroactivePointsUsageModal] = useState(false);
	const [showSettlePaymentModal, setShowSettlePaymentModal] = useState(false);
	const [copyFlashReservationId, setCopyFlashReservationId] = useState(false);
	const [copyFlashPaymentId, setCopyFlashPaymentId] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
    const [loadingVoucher, setLoadingVoucher] = useState(false);

    const { name: hotelName = '', city: hotelCity = '', address: hotelAddress = '', country: hotelCountry = ''} = booking?.hotel || {};

	const now_utc = useMemo(() => moment.utc(), []); // Memoize the current UTC moment

	const { travel_booster_integration_enabled = false } = profile.settings;
	const { combtas_integration_enabled = false } = profile.company.settings;
	const fullyRefundableTill = _.get(booking, 'deal.dca.dealIsFullyRefundableTill');
	const isConfirmed = _.get(booking, 'confirmed');
	const cancelStatus = _.get(booking, 'cancelStatus');
	const isShowInvoice = _.get(booking, 'showInvoice');
	const isFutureReservation =
		booking && now_utc.isBefore(moment.utc(_.get(booking, 'deal.dca.dealIsFullyRefundableTill')));
	const isGCashApp = _.get(booking, 'payment.payment_type') === PAYMENT_TYPE.GCASH_APP;
	const isElal = isElalCompany();
	// Travel Booster data
	const docketNumber =
		_.get(booking, 'integrations.travel_booster.docket_number') || _.get(booking, 'travel_booster_docket_number');
	const travelBoosterSynced = _.get(booking, 'travel_booster_synced');

	const retroactive_points_usage_enabled = isRetroactivePointsUsageEnabled({ profile, booking });

	const isExpediaPartner = _.get(profile, 'company.partners_ui.expedia_partner', false);
	const isGDSCompany =
		_.get(profile, 'company.settings.enable_sabro_gds_string_generation', false) ||
		_.get(profile, 'company.settings.enable_amadeus_gds_string_generation', false);

	const drawerRef = useScrollToTopOnBookingChange(booking);
	const isHTML = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

	useEffect(() => {
		setIsScrolled(false);
	}, [booking]);

	const is_agent = useMemo(() => {
		return profile.agent || profile.super_agent;
	}, [profile]);

	const is_agent_or_tm = useMemo(() => {
		return profile.agent || profile.super_agent || profile.freelance_agent || profile.travel_manager;
	}, [profile]);

	const in_cancel_policy = useMemo(() => {
		return (
			!_.get(booking, 'deal.dca.nonRefundable') &&
			fullyRefundableTill &&
			now_utc.isSameOrBefore(moment.utc(fullyRefundableTill))
		);
	}, [booking, fullyRefundableTill, now_utc]);

	const canCancelReservation = useMemo(() => {
		return in_cancel_policy && isConfirmed && isFutureReservation;
	}, [in_cancel_policy, isConfirmed, isFutureReservation]);

	const bankTransferLocked = isBankTransferLocked(booking);
	const bankTransferPaid = isBankTransferPaid(booking);

	// AR-3488 AC #4 — reason banner. `needsBankTransferCorrection` /
	// `getBankTransferRejectionReason` etc. all degrade to `null`/`false`
	// before 107-BE lands (or for a booking with no bank-transfer request at
	// all), so this block renders nothing rather than throwing.
	const showBankTransferCorrectionBanner = needsBankTransferCorrection(booking);
	const bankTransferRejectionReason = getBankTransferRejectionReason(booking);
	const bankTransferRejectionComment = getBankTransferRejectionComment(booking);
	const bankTransferReferenceNumber = getBankTransferReferenceNumber(booking);

	const shouldPay = useMemo(() => {
		return getIsUnpaid(profile, booking) && !bankTransferLocked;
	}, [profile, booking, bankTransferLocked]);

	const isCompleted = useMemo(() => {
		if (!booking) return false;
		return BookingStatus.isCompletedStatus(booking.status, booking?.deal?.details?.check_out);
	}, [booking]);

	const isBnpl = useMemo(() => getIsBnpl(booking), [booking]);
	const isBnplCharged = _.get(booking, 'payment.status') === PAYMENT_STATUS.BILLED;
	const isBnplCancelled = _.get(booking, 'payment.status') === PAYMENT_STATUS.CANCELLED;

	const bnplLinkState = useMemo(() => {
		if (!isBnpl || isBnplCharged || isBnplCancelled) return null;
		const expired = settlePaymentHelpers.isTokenExpired(booking);
		const link = settlePaymentHelpers.getPaymentLink(booking);
		return { link: !expired && link ? link : '', expired };
	}, [isBnpl, isBnplCharged, isBnplCancelled, booking]);

	const isBnplFailed = !!booking?.bnpl_self_service_eligible;

	const openBnplPaymentLink = () => {
		if (bnplLinkState?.link) {
			window.open(bnplLinkState.link, '_blank', 'noopener');
		}
	};

	const showBnplAllSet = isBnpl && !isBnplCharged && !isBnplCancelled;

	const bnplChargeDateFormatted = useMemo(() => {
		const charge_date = _.get(booking, 'payment.charge_date');
		return charge_date ? moment.utc(charge_date).format('D MMM YYYY') : null;
	}, [booking]);

	const bnplCancellationDeadlineFormatted = useMemo(() => {
		return fullyRefundableTill ? moment.utc(fullyRefundableTill).format('MMM D, YYYY hh:mm A') + ' (GMT)' : null;
	}, [fullyRefundableTill]);

	const bnplAutoCancelDate = useMemo(() => {
		const next_retry_date = _.get(booking, 'payment.bnpl.next_retry_date');
		if (!next_retry_date) return null;
		const attempt_count = _.get(booking, 'payment.bnpl.attempts', []).length;
		const remaining_retries = Math.max(0, BNPL_MAX_CHARGE_ATTEMPTS - attempt_count - 1);
		return moment.utc(next_retry_date).add(remaining_retries, 'days').format('D MMM YYYY');
	}, [booking]);

	const bnplDaysUntilCharge = useMemo(() => {
		const charge_date = _.get(booking, 'payment.charge_date');
		if (!charge_date) return null;
		return moment.utc(charge_date).startOf('day').diff(moment.utc().startOf('day'), 'days');
	}, [booking]);

	const showBnplChargeReminder =
		showBnplAllSet &&
		!isBnplFailed &&
		_.isNumber(bnplDaysUntilCharge) &&
		bnplDaysUntilCharge >= 0 &&
		bnplDaysUntilCharge <= BNPL_CHARGE_REMINDER_DAYS;

	useEffect(() => {
		if (cancelStatus === 'succeeded') {
			ReservationActions.resetReservationCancellationStatus(booking.id);
			setShowCancelSuccess(true);
			scrollToTop();
			setTimeout(() => {
				setShowCancelSuccess(false);
			}, 4000);
		}
	}, [canCancelReservation]);

	const scrollToTop = () => {
		if (drawerRef.current) {
			drawerRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	};

	const cancellation_policy_text = useMemo(() => {
		const multiLanguageTexts = _.get(booking, 'deal.dca.cancellationPoliciesMultiLanguageTexts');
		const defaultText = _.get(booking, 'deal.dca.cancellationPoliciesText');

		return multiLanguageTexts ? multiLanguageTexts[i18n.language] : defaultText;
	}, [booking, i18n.language]);

	const breakfastIncluded = useMemo(() => {
		return getBreakfastIncluded(booking);
	}, [booking]);

	const onShowInvoice = () => {
		Analytics.actions.interactions.downloadedInvoice(
			profile,
			booking.id,
			booking.invoice_url,
			{
                name: hotelName,
                city: hotelCity,
                address: hotelAddress,
                country: hotelCountry
            },
			booking.deal.details,
		);
		window.open(booking.invoice_url, '_blank', 'noopener noreferrer');
	};

    const onShowVoucher = () => {
        setLoadingVoucher(true);

        const apiUrl = getVoucherURL(booking);

        fetch(apiUrl, { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                Analytics.actions.interactions.showedVoucher(
                    profile,
                    booking.id,
                    document.location.protocol + apiUrl,
                    booking.hotel,
                    booking.deal.details
                );
                window.open(data.downloadLink,'noopener,noreferrer');
            })
            .catch(error => {
                console.error('Error fetching voucher:', error);
            })
            .finally(() => {
                setLoadingVoucher(false);
            });
            
    };

	const onSendBookingConfirmation = () => {
		const deal = _.assign({}, booking.deal.details, booking.deal.cheapopoPricing, booking.deal.dca);

		ReservationActions.sendBookingConfirmation(booking.id, profile);
		Analytics.actions.interactions.sentBookingConfirmation(booking.id, booking.invoice_url, booking.hotel, deal);
	};

	const copyReservationId = () => {
		setCopyFlashReservationId(true);
		navigator.clipboard.writeText(booking.short_id);

		setTimeout(() => {
			setCopyFlashReservationId(false);
		}, 4000);
	};

	const copyPaymentId = () => {
		setCopyFlashPaymentId(true);
		navigator.clipboard.writeText(booking.payment._id);

		setTimeout(() => {
			setCopyFlashPaymentId(false);
		}, 4000);
	};

	const isBusy = () => confirmationStatus === BookingsConstants.STATUS.BUSY;

	if (!booking) {
		console.log('BookingDrawer ::: NO BOOKING');
		return null;
	}

	const points_applicable_date_time = moment.utc(booking.deal.details.check_out).add(1, 'day');
	const now = moment.utc();
	const points_upcoming_tooltip_text = points_applicable_date_time.isAfter(now)
		? `Points will be applicable at ${points_applicable_date_time.format('MMM D YYYY')} 11:59 PM (GMT)`
		: 'Points are applicable';

	const openRetroactiveModal = retroactive_points_usage_enabled
		? () => {
				setShowRetroactivePointsUsageModal(true);
				Analytics.actions.interactions.viewedPointsRedeemModal(booking.id);
			}
		: _.noop;

	const openSettlePaymentModal = profile.freelance_agent
		? () => {
				setShowSettlePaymentModal(true);
				Analytics.actions.interactions.openPayLaterModal(booking.id);
			}
		: _.noop;

	const PAYMENT_DETAILS = Config.is_elal ? null :
		booking.payment.payment_type &&
		(PriceBreakdown.isBreakdownPrice({ profile, reservation: booking }) ? (
			<BookingDrawerPaymentBreakdownDetails booking={booking} />
		) : (
			<BookingDrawerPaymentDetails
				booking={booking}
				retroactivePointsUsageEnabled={retroactive_points_usage_enabled}
				openRetroactiveModal={openRetroactiveModal}
				profile={profile}
			/>
		));

	const onKeyDown = (e) => {
		if (e.keyCode === ESCAPE_KEY_CODE) {
			if (showRetroactivePointsUsageModal) {
				Analytics.actions.interactions.closedPointsModal(booking.id);
				setShowRetroactivePointsUsageModal(false);
			} else if (showSettlePaymentModal) {
				Analytics.actions.interactions.closedPayLaterModal(booking.id);
				setShowSettlePaymentModal(false);
			}
		}
	};

	const getPayLaterDeadlineFormat = () => {
		return moment(booking.pay_later_deadline).format(date_format);
	};

	const handleScroll = (e) => {
		const scrollTop = e.target.scrollTop;
		setIsScrolled(scrollTop > 0);
	};

	const isRTL = i18n.dir() === RTL;

	return (
        <React.Fragment>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                className="booking-drawer"
                sx={{
                    '& .MuiDrawer-paper': {
                        direction: isRTL ? 'rtl' : 'ltr',
                        left: isRTL ? '0 !important' : 'auto',
                        right: isRTL ? 'auto' : '0 !important',
                        transform: 'none !important',
                    },
                }}
            >
                <div ref={drawerRef} tabIndex="-1" className="booking-drawer-inner" data-testid="booking-drawer" onScroll={handleScroll}>
                    <div className={`booking-title ${isScrolled ? 'shadow' : ''}`}>
                        <div className="flex space-between align-items-baseline">
                            <div className="trip-to">{hotelName}</div>
                            <IconButton className="close-drawer-button" onClick={onClose} aria-label="Close">
                                <CloseOutlined />
                            </IconButton>
                        </div>
                        <div className="city">
                            <div>{hotelCity}</div>
                            <div>{[hotelAddress, hotelCity, hotelCountry].filter(Boolean).join(', ')}</div>
                        </div>
                        <div className="flex space-between">
                            <div>
                                <div
                                    className={`booking-status ${shouldPay ? 'unpaid' : isCompleted ? 'completed' : booking.status} ${
                                        bankTransferLocked ? 'bank-transfer-locked' : ''
                                    }`}
                                >
                                    <span>● {BookingStatus.getBookingStatus(booking.status, t, booking?.deal?.details?.check_out)}</span>
                                    {shouldPay && <span className="booking-status pending gap">{t('Unpaid')}</span>}
                                    {bankTransferLocked && (
                                        <span className={`booking-status gap ${bankTransferPaid ? 'confirmed' : 'pending'}`}>
                                            {t(getBankTransferLockTranslationKey(booking))}
                                        </span>
                                    )}
                                </div>
                                {shouldPay && (
                                    <div className="booking-status pending">
                                        {t('PaymentDeadline')} {getPayLaterDeadlineFormat()}
                                    </div>
                                )}
                            </div>
                            <div>
                                {profile.freelance_agent && shouldPay && (
                                    <div>
                                        <Button variant="contained" fullWidth onClick={openSettlePaymentModal}>
                                            {/* Settle Payment */}
                                            {t('Pay')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {showBankTransferCorrectionBanner && (
                        <div className="bank-transfer-correction-banner" data-testid="bank-transfer-correction-banner">
                            <WarningAmberIcon className="bank-transfer-correction-banner__icon" />
                            <div className="bank-transfer-correction-banner__content">
                                <div className="bank-transfer-correction-banner__title">
                                    {t('BankTransferNeedsCorrectionTitle')}
                                </div>
                                <div className="bank-transfer-correction-banner__body">
                                    {t('BankTransferNeedsCorrectionBody')}
                                </div>
                                {bankTransferRejectionReason && (
                                    <div className="bank-transfer-correction-banner__row">
                                        <span className="bank-transfer-correction-banner__label">
                                            {t('BankTransferRejectionReasonLabel')}:
                                        </span>{' '}
                                        {t(`BankTransferRejectionReason.${bankTransferRejectionReason}`, {
                                            defaultValue: bankTransferRejectionReason,
                                        })}
                                    </div>
                                )}
                                {bankTransferRejectionComment && (
                                    <div className="bank-transfer-correction-banner__row">
                                        <span className="bank-transfer-correction-banner__label">
                                            {t('BankTransferRejectionCommentLabel')}:
                                        </span>{' '}
                                        {bankTransferRejectionComment}
                                    </div>
                                )}
                                {bankTransferReferenceNumber && (
                                    <div className="bank-transfer-correction-banner__row">
                                        <span className="bank-transfer-correction-banner__label">
                                            {t('BankTransferReferenceNumberLabel')}:
                                        </span>{' '}
                                        {bankTransferReferenceNumber}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {showBnplChargeReminder && (
                        <div className="bnpl-charge-reminder">
                            <InfoIcon className="bnpl-charge-reminder__icon" />
                            <span>
                                {t('BnplChargeReminderPre')}
                                <strong>{t('BnplChargeReminderBold', { days: bnplDaysUntilCharge })}</strong>
                            </span>
                        </div>
                    )}

                    {showBnplAllSet && (
                        <div className={`bnpl-all-set${isBnplFailed ? ' bnpl-all-set--with-alert' : ''}`}>
                            <div className="bnpl-all-set__title">{t('BnplAllSetTitle')}</div>
                            <div className="bnpl-all-set__row">
                                <BnplNoChargesIcon className="bnpl-all-set__icon" />
                                <span>{t('BnplAllSetNoCharges', { date: bnplChargeDateFormatted })}</span>
                            </div>
                            {bnplCancellationDeadlineFormatted && (
                                <div className="bnpl-all-set__row">
                                    <FreeCancelationIcon className="bnpl-all-set__icon" />
                                    <span>{t('BnplAllSetFreeCancellation', { date: bnplCancellationDeadlineFormatted })}</span>
                                </div>
                            )}
                            <div className="bnpl-all-set__row">
                                <Check className="bnpl-all-set__icon" />
                                <span>{t('BnplAllSetSecured')}</span>
                            </div>
                            {isBnplFailed && (
                                <div className="bnpl-payment-alert">
                                    <div className="bnpl-payment-alert__content">
                                        <span className="bnpl-payment-alert__title">{t('BnplPaymentAlertTitle')}</span>
                                        <div className="bnpl-payment-alert__body">
                                            {t('BnplPaymentAlertBodyPre')}
                                            <strong>{t('BnplPaymentAlertBodyBold', { date: bnplAutoCancelDate })}</strong>
                                        </div>
                                    </div>
                                    {bnplLinkState?.link && (
                                        <button
                                            type="button"
                                            className="bnpl-pay-now-btn bnpl-pay-now-btn--danger"
                                            onClick={openBnplPaymentLink}
                                        >
                                            {t('BnplPayNow')}
                                        </button>
                                    )}
                                </div>
                            )}
                            {!isBnplFailed && bnplLinkState?.link && (
                                <div className="bnpl-all-set__pay-now-footer">
                                    <div>
                                        <div className="bnpl-all-set__ready-to-pay-title">{t('BnplAllSetReadyToPayTitle')}</div>
                                        <div className="bnpl-all-set__ready-to-pay-sub">{t('BnplAllSetReadyToPaySub')}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className="bnpl-pay-now-btn bnpl-pay-now-btn--brand"
                                        onClick={openBnplPaymentLink}
                                    >
                                        {t('BnplPayNow')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {isGDSCompany && <BookingDrawerGDSComponent booking={booking} profile={profile} />}

                    <BookingDrawerReservationSummary
                        booking={booking}
                        totalPrice={
                            PriceBreakdown.isBreakdownPrice({ profile, reservation: booking })
                                ? booking.deal.expedia_pricing.totalPrice
                                : 0
                        }
                        isElal={isElal}
                    />

                    {profile.loyalty.enabled &&
                        booking.arbitrip_loyalty_earned &&
                        _.isNumber(booking.arbitrip_loyalty_earned.points) &&
                        booking.arbitrip_loyalty_earned.points > 0 && (
                            <BookingField
                                fieldTitle={t('LoyaltyProgram')}
                                // fieldValue={booking.short_id}
                                fieldValue={
                                    <div className="booking-points">
                                        {/* TODO: when not yet eligible-  update text from earned to eligible when... (instead of hover) */}
                                        <div className="booking-points-value">
                                            {booking.arbitrip_loyalty_earned.points}{' '}
                                            {booking.arbitrip_loyalty_earned.points === 1 ? t('point') : t('points')}{' '}
                                            {t('earned')}
                                        </div>
                                        {/* <div className="booking-points-discount">$50 discount</div> */}
                                        {/* <div className="booking-points-icon"><PointsIcon /></div> */}
                                    </div>
                                }
                                // icon={<Tooltip PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, -10] } }] }} placement='top' className='points-upcoming-tooltip' title={points_upcoming_tooltip_text}>
                                //     <IconButton>
                                //         <InfoIcon />
                                //     </IconButton>
                                // </Tooltip>}
                                icon={
                                    <Tooltip
                                        PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, -10] } }] }}
                                        placement="top"
                                        className="points-upcoming-tooltip"
                                        title={points_upcoming_tooltip_text}
                                    >
                                        <IconButton>
                                            <div className="booking-points-icon">
                                                <PointsIcon />
                                            </div>
                                        </IconButton>
                                    </Tooltip>
                                }
                            />
                        )}

                    <FutureBenefits
                        earnedDiamonds={booking.elal_loyalty_earned?.accumulated_diamonds}
                        earnedPoints={booking.elal_loyalty_earned?.accumulated_points}
                    />

                    {retroactive_points_usage_enabled && PAYMENT_DETAILS}

                    <BookingField
                        data-testid="booking-drawer-cancellation-policy"
                        fieldTitle={t('CancellationPolicy')}
                        fieldValue={
                            cancellation_policy_text ? (
                                <CollapseText
                                    text={cancellation_policy_text}
                                    breakLines
                                    showMoreText={t('ShowMore')}
                                    showLessText={t('ShowLess')}
                                />
                            ) : (
                                t('None')
                            )
                        }
                    />

                    <BookingField
                        data-testid="booking-drawer-room"
                        fieldTitle={
                            booking.deal.details.room_count > 1
                                ? t('RoomPluralWithCount', { count: booking.deal.details.room_count })
                                : t('Room')
                        }
                        fieldValue={
                            <React.Fragment>
                                {booking.deal.details.room_description}
                                {breakfastIncluded && (
                                    <div className="space">
                                        <BreakfastIcon />
                                        {Catering.getCatering(booking.deal.dca.heuristicOptions)}
                                    </div>
                                )}
                            </React.Fragment>
                        }
                    />

                    <TravelersField
                                      booking={booking
                                  }
                                t={t}
                        className="capitalize"
                    />

                    {is_agent && booking.company && (
                        <BookingField fieldTitle={t('ClientName')} fieldValue={booking.company.name} />
                    )}

                    {is_agent && booking.handling_agent && (
                        <BookingField fieldTitle={t('HandlingAgent')} fieldValue={booking.handling_agent.full_name} />
                    )}

                    {booking.short_id && (
                        <BookingField
                            fieldTitle={t('ReservationID')}
                            fieldValue={booking.short_id}
                            icon={<CopyIcon />}
                            onClick={copyReservationId}
                            className={{ active: copyFlashReservationId }}
                        />
                    )}

                    {display_dev && booking.payment._id && (
                        <BookingField
                            fieldTitle={t('PaymentID')}
                            fieldValue={booking.payment._id}
                            icon={<CopyIcon />}
                            onClick={copyPaymentId}
                            className={{ active: copyFlashPaymentId }}
                        />
                    )}

                    {!retroactive_points_usage_enabled && PAYMENT_DETAILS}

                    {!_.isEmpty(booking.creator) && (
                        <BookingField fieldTitle={t('Creator')} fieldValue={booking.creator.full_name} />
                    )}

                    <BookingField
                        fieldTitle={t('CreationDate')}
                        fieldValue={moment(booking.creation_date).format(date_format)}
                    />

                    {booking.specialRequests && (
                        <BookingField fieldTitle={t('SpecialRequests')} fieldValue={booking.specialRequests} />
                    )}

                    <BookingField
                        data-testid="booking-drawer-terms-of-supplier"
                        fieldTitle={t('TermsOfSupplier')}
                        fieldValue={
                            booking.supplierTerms ? (
                                isHTML(booking.supplierTerms) ? (
                                    <div
                                        className="supplier-terms"
                                        dangerouslySetInnerHTML={{ __html: booking.supplierTerms }}
                                    />
                                ) : (
                                    <CollapseText
                                        text={booking.supplierTerms}
                                        breakLines
                                        showMoreText={t('ShowMore')}
                                        showLessText={t('ShowLess')}
                                    />
                                )
                            ) : (
                                'None'
                            )
                        }
                    />

                    {combtas_integration_enabled && (is_agent_or_tm || (!is_agent_or_tm && booking?.combtas_id)) && (
                        <BookingDrawerTasId
                            sync_combtas_status={sync_combtas_status}
                            booking={booking}
                            is_agent_or_tm={is_agent_or_tm}
                        />
                    )}

                    {docketNumber && travelBoosterSynced && (
                        <BookingField fieldTitle={t('TravelBooster.DocketNumber')} fieldValue={docketNumber} />
                    )}
                    {isElal && isConfirmed && (
                        <div className="booking-disclaimer">
                            <div className="booking-disclaimer-title">{t('BookingDisclaimer.Title')}</div>
                            <div className="booking-disclaimer-text">{t('BookingDisclaimer.Description')}</div>
                            <div className="booking-disclaimer-text">{t('BookingDisclaimer.Suffix')}</div>
                        </div>
                    )}
                    <div className="actions column">
                        {
                            // if the payment done through GCash - don't show invoice button
                            !isGCashApp && !isExpediaPartner && isShowInvoice && booking.invoice_url && (
                                <Button variant="text" onClick={onShowInvoice} startIcon={<InvoiceIcon/>}>
                                    {t('ShowInvoice')}
                                </Button>
                            )
                        }

                        {isConfirmed && booking.has_voucher && !booking.deal.details.post_pay && (
                            <Button 
                                disabled={loadingVoucher} 
                                variant="text" 
                                onClick={onShowVoucher} 
                                startIcon={ loadingVoucher? <CircularProgress  className='loader' /> : <VoucherIcon />}
                            >
                                {booking.has_voucher ? t('ShowVoucher') : t('IssueVoucher')}
                            </Button>
                        )}
                    </div>

                    {travel_booster_integration_enabled && (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setShowTravelBoosterModal(true)}>
                                {t('TravelBooster.Title')}
                            </Button>
                        </div>
                    )}

                    <div className="actions">
                        {(profile.agent || profile.travel_manager) && (
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={onSendBookingConfirmation}
                                disabled={!isConfirmed || isBusy()}
                            >
                                {t('SendBookingConfirmation')}
                                {isBusy() && <CircularProgress className="circular-progress" size={12} />}
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="danger"
                            fullWidth
                            onClick={() => setShowCancelModal(true)}
                            disabled={!canCancelReservation}
                            data-testid="booking-cancel-reservation"
                        >
                            {t('CancelReservation')}
                        </Button>
                    </div>
                </div>
            </Drawer>

            {canCancelReservation && (
                <CancelReservationDialog
                    open={
                        booking.status !== ReservationConstants.RESERVATION_STATUS.CANCELED &&
                        (showCancelModal ||
                            booking.status === ReservationConstants.RESERVATION_STATUS.CANCELING ||
                            booking.cancelStatus === ReservationConstants.STATUS.FAILED)
                    }
                    onClose={() => setShowCancelModal(false)}
                    booking={booking}
                    points={points}
                    profile={profile}
                    in_cancel_policy={in_cancel_policy}
                />
            )}

            {travel_booster_integration_enabled && (
                <MaterialDesignDialog
                    open={showTravelBoosterModal}
                    onClose={() => setShowTravelBoosterModal(false)}
                    closeTimeoutInMilliseconds={150}
                    key={`travel-booster-modal-${booking.id}`}
                    title={<TravelBoosterTitle onClose={() => setShowTravelBoosterModal(false)} />}
                    content={
                        <TravelBooster
                            booking={booking}
                            syncTravelBoosterStatus={syncTravelBoosterStatus}
                            onClose={() => setShowTravelBoosterModal(false)}
                            profile={profile}
                        />
                    }
                />
            )}

            {retroactive_points_usage_enabled && (
                <MaterialDesignDialog
                    open={showRetroactivePointsUsageModal}
                    onClose={() => setShowRetroactivePointsUsageModal(false)}
                    closeTimeoutInMilliseconds={150}
                    key={`retroactive-points-usage-modal-${booking.id}`}
                    title={
                        <RetroactivePointsUsageTitle
                            onClose={() => setShowRetroactivePointsUsageModal(false)}
                            booking={booking}
                        />
                    }
                    content={
                        <RetroactivePointsUsage
                            booking={booking}
                            profile={profile}
                            points={points}
                            updatePointsUsageStatus={updatePointsUsageStatus}
                            onClose={() => setShowRetroactivePointsUsageModal(false)}
                        />
                    }
                    onKeyDown={onKeyDown}
                    minWidth={825}
                />
            )}

            {profile.freelance_agent && (
                <SettlePaymentMain
                    booking={booking}
                    profile={profile}
                    onKeyDown={onKeyDown}
                    settlePaymentStatus={settlePaymentStatus}
                    showSettlePaymentModal={showSettlePaymentModal}
                    setShowSettlePaymentModal={setShowSettlePaymentModal}
                />
            )}

            <Snackbar open={showCancelSuccess} message={t('Bookings.Cancellation.Succeeded')} autoHideDuration={4000} />

            <Snackbar
                open={copyFlashReservationId}
                message={t('Bookings.Copy.ReservationID')}
                autoHideDuration={4000}
            />
            <Snackbar open={copyFlashPaymentId} message={t('Bookings.Copy.PaymentID')} autoHideDuration={4000} />
        </React.Fragment>
    );
};

export default BookingDrawer;
